import {
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';

type MetrcAuthData = {
  market: string;
  marketId: number;
  username?: string;
};

// 'reauth' signs in again from outside the import wizard (profile, package import, relink) and returns to returnTo.
export type MetrcImportWorkflow = 'facilities' | 'products' | 'integration' | 'reauth';

type MetrcFacilitySelection = import('../../types').MetrcFacilityDocument;

type ImportsWizardContextValue = {
  metrcAuthData: MetrcAuthData | null;
  metrcFacilitiesSelected: MetrcFacilitySelection[];
  returnTo: string;
  workflow: MetrcImportWorkflow;
  resetMetrcWizard: () => void;
  setMetrcAuthData: (value: MetrcAuthData | null) => void;
  setMetrcFacilitiesSelected: (value: MetrcFacilitySelection[]) => void;
  setReturnTo: (value: string) => void;
  setWorkflow: (value: MetrcImportWorkflow) => void;
};

const ImportsWizardContext = createContext<ImportsWizardContextValue | null>(null);

export function ImportsWizardProvider({ children }: { children: ReactNode }) {
  const [metrcAuthData, setMetrcAuthData] = useState<MetrcAuthData | null>(null);
  const [metrcFacilitiesSelected, setMetrcFacilitiesSelected] = useState<MetrcFacilitySelection[]>([]);
  const [returnTo, setReturnTo] = useState('/imports');
  const [workflow, setWorkflow] = useState<MetrcImportWorkflow>('integration');

  const value = useMemo(
    () => ({
      metrcAuthData,
      metrcFacilitiesSelected,
      returnTo,
      workflow,
      resetMetrcWizard: () => {
        setMetrcAuthData(null);
        setMetrcFacilitiesSelected([]);
        setReturnTo('/imports');
        setWorkflow('integration');
      },
      setMetrcAuthData,
      setMetrcFacilitiesSelected,
      setReturnTo,
      setWorkflow,
    }),
    [metrcAuthData, metrcFacilitiesSelected, returnTo, workflow]
  );

  return (
    <ImportsWizardContext.Provider value={value}>
      {children}
    </ImportsWizardContext.Provider>
  );
}

export function useImportsWizard() {
  const context = useContext(ImportsWizardContext);
  if (!context) {
    throw new Error('useImportsWizard must be used within ImportsWizardProvider');
  }
  return context;
}
